import { EventBus, EventBusListeners } from "@zyzgroup/core-common";
import Hammer from "hammerjs";
import { useEventListener } from "./useEventListener";
import {
  MousePointEvent,
  getMousePositionInElement,
  isSupportTouch
} from "./useElementEvents";
import { useResize } from "./useResize";

// deprecated 已过时！建议切换至：./useElementEvents.ts

declare const document: Document & {
  attachEvent: any;
  createEventObject: any;
  onmousewheel: any;
};

type MousePoint = {
  x: number;
  y: number;
  dx: number;
  dy: number;
  dStartX: number;
  dStartY: number;
  isDown: boolean; // 是否已按下
};

function useMousePoint() {
  const startMousePoint: MousePoint = {
    x: 0,
    y: 0,
    dx: 0,
    dy: 0,
    dStartX: 0,
    dStartY: 0,
    isDown: false
  };

  const currentMousePoint: MousePoint = {
    x: 0,
    y: 0,
    dx: 0,
    dy: 0,
    dStartX: 0,
    dStartY: 0,
    isDown: false
  };

  return {
    mousePoint: currentMousePoint,

    setStartMousePoint: function (
      p: { x: number; y: number },
      isDown?: boolean
    ) {
      startMousePoint.x = p.x;
      startMousePoint.y = p.y;
      startMousePoint.isDown = typeof isDown == "boolean" ? isDown : false;
      this.setCurrentMousePoint(p, isDown);
    },

    setCurrentMousePoint: (p: { x: number; y: number }, isDown?: boolean) => {
      const prevX = currentMousePoint.x;
      const prevY = currentMousePoint.y;
      currentMousePoint.x = p.x;
      currentMousePoint.y = p.y;
      currentMousePoint.dx = currentMousePoint.x - prevX;
      currentMousePoint.dy = currentMousePoint.y - prevY;
      currentMousePoint.dStartX = currentMousePoint.x - startMousePoint.x;
      currentMousePoint.dStartY = currentMousePoint.y - startMousePoint.y;
      currentMousePoint.isDown = typeof isDown == "boolean" ? isDown : false;
    }
  };
}

export interface ElementEventBusListeners extends EventBusListeners {
  mouseDown(e: MouseEvent): void;
  mouseUp(e: MouseEvent): void;
  mouseMove(e: MouseEvent): void;
  mouseOver(e: MouseEvent): void;
  mouseOut(e: MouseEvent): void;
  mouseEnter(e: MouseEvent): void;
  mouseLeave(e: MouseEvent): void;
  mouseClick(e: MouseEvent): void;
  mouseDoubleClick(e: MouseEvent): void;
  mouseWheel(e: WheelEvent): void;
  mouseContextMenu(e: MouseEvent): void;

  touchStart(e: TouchEvent): void;
  touchEnd(e: TouchEvent): void;
  touchCancel(e: TouchEvent): void;
  touchMove(e: TouchEvent): void;

  pointerDown(e: PointerEvent): void;
  pointerUp(e: PointerEvent): void;
  pointerCancel(e: PointerEvent): void;
  pointerMove(e: PointerEvent): void;

  /// https://hammerjs.github.io/
  // Pan : Recognized when the pointer is down and moved in the allowed direction.
  pan(e: HammerInput): void;
  panStart(e: HammerInput): void;
  panMove(e: HammerInput): void;
  panEnd(e: HammerInput): void;
  panCancel(e: HammerInput): void;
  panLeft(e: HammerInput): void;
  panRight(e: HammerInput): void;
  panUp(e: HammerInput): void;
  panDown(e: HammerInput): void;

  // 缩放 Pinch : Recognized when two or more pointers are moving toward (zoom-in) or away from each other (zoom-out).
  pinch(e: HammerInput): void;
  pinchin(e: HammerInput): void;
  pinchout(e: HammerInput): void;

  // 长按 Press : Recognized when the pointer is down for x ms without any movement.
  press(e: HammerInput): void;

  // 旋转 Rotate : Recognized when two or more pointer are moving in a circular motion.
  rotate(e: HammerInput): void;

  // Swipe : Recognized when the pointer is moving fast (velocity), with enough distance in the allowed direction.
  swipe(e: HammerInput): void;
  swipeLeft(e: HammerInput): void;
  swipeRight(e: HammerInput): void;
  swipeUp(e: HammerInput): void;
  swipeDown(e: HammerInput): void;

  // Tap : Recognized when the pointer is doing a small tap/click.
  tap(e: HammerInput): void;

  // 在 el 之外点击
  tapOutSide(e: Event): void;

  transitionstart(e: TransitionEvent): void;
  transitionend(e: TransitionEvent): void;
  animationstart(e: AnimationEvent): void;
  animationend(e: AnimationEvent): void;

  resize: (rect: DOMRect) => void;
}

export type ElementEventBus = EventBus<ElementEventBusListeners>;

export type ElementEventBusController = ReturnType<typeof useElementEventBus>;

export function useElementEventBus(el: HTMLElement = document.documentElement) {
  const eb = new EventBus<ElementEventBusListeners>();
  const hammer = new Hammer(el);
  let removes: (() => void)[] = [];

  const ump = useMousePoint();
  const mousePoint = ump.mousePoint;

  const mouseDown = (e: MouseEvent) => {
    // 影响性能
    // el.style.userSelect = "none"; // if there's text
    eb.emit("mouseDown", e);
  };
  const mouseUp = (e: MouseEvent) => {
    // 影响性能
    // el.style.userSelect = "";
    eb.emit("mouseUp", e);
  };
  const mouseMove = (e: MouseEvent) => {
    if (!mousePoint.isDown) {
      ump.setCurrentMousePoint(getMousePositionInElement(e, el));
    }
    eb.emit("mouseMove", e);
  };
  /// 如果触发的元素内部有其他的元素的时候当鼠标移上的时候会反复的触发 mouseover 和 mouseout 事件
  /// 因为内部元素在鼠标移上的时候会向它的父对象派发事件，所以外面元素相当于也触发了mouseover 事件
  const mouseOver = (e: MouseEvent) => {
    eb.emit("mouseOver", e);
  };
  const mouseOut = (e: MouseEvent) => {
    eb.emit("mouseOut", e);
  };
  const mouseEnter = (e: MouseEvent) => {
    eb.emit("mouseEnter", e);
  };
  const mouseLeave = (e: MouseEvent) => {
    eb.emit("mouseLeave", e);
  };
  const mouseClick = (e: MouseEvent) => {
    eb.emit("mouseClick", e);
  };
  const mouseDoubleClick = (e: MouseEvent) => {
    eb.emit("mouseDoubleClick", e);
  };
  const mouseWheel = (e: WheelEvent) => {
    // const delta = e.deltaY || -e.wheelDelta || e.detail;
    eb.emit("mouseWheel", e);
  };
  const mouseContextMenu = (e: MouseEvent) => {
    eb.emit("mouseContextMenu", e);
  };

  const touchStart = (e: TouchEvent) => {
    // const touch = e.changedTouches[0]; // first touch
    eb.emit("touchStart", e);
  };
  const touchEnd = (e: TouchEvent) => {
    // const touch = e.changedTouches[0]; // first touch
    eb.emit("touchEnd", e);
  };
  const touchCancel = (e: TouchEvent) => {
    // const touch = e.changedTouches[0]; // first touch
    eb.emit("touchCancel", e);
  };
  const touchMove = (e: TouchEvent) => {
    // e.preventDefault(); // prevent scroll
    const touch = e.changedTouches[0]; // first touch
    if (!mousePoint.isDown) {
      ump.setCurrentMousePoint(getMousePositionInElement(touch, el));
    }
    eb.emit("touchMove", e);
  };

  const pointerDown = (e: PointerEvent) => {
    // el.setPointerCapture(e.pointerId); // 捕获后续 pointermove / pointerup
    eb.emit("pointerDown", e);
  };
  const pointerUp = (e: PointerEvent) => {
    // el.releasePointerCapture(e.pointerId);
    eb.emit("pointerUp", e);
  };
  const pointerCancel = (e: PointerEvent) => {
    // el.releasePointerCapture(e.pointerId);
    eb.emit("pointerCancel", e);
  };
  const pointerMove = (e: PointerEvent) => {
    eb.emit("pointerMove", e);
  };

  const pan = (e: HammerInput) => {
    eb.emit("pan", e);
  };
  const panStart = (e: HammerInput) => {
    eb.emit("panStart", e);
  };
  const panMove = (e: HammerInput) => {
    eb.emit("panMove", e);
  };
  const panEnd = (e: HammerInput) => {
    eb.emit("panEnd", e);
  };
  const panCancel = (e: HammerInput) => {
    eb.emit("panCancel", e);
  };
  const panLeft = (e: HammerInput) => {
    eb.emit("panLeft", e);
  };
  const panRight = (e: HammerInput) => {
    eb.emit("panRight", e);
  };
  const panUp = (e: HammerInput) => {
    eb.emit("panUp", e);
  };
  const panDown = (e: HammerInput) => {
    eb.emit("panDown", e);
  };

  const pinch = (e: HammerInput) => {
    eb.emit("pinch", e);
  };
  const pinchin = (e: HammerInput) => {
    eb.emit("pinchin", e);
  };
  const pinchout = (e: HammerInput) => {
    eb.emit("pinchout", e);
  };

  const press = (e: HammerInput) => {
    eb.emit("press", e);
  };

  const rotate = (e: HammerInput) => {
    eb.emit("rotate", e);
  };

  const swipe = (e: HammerInput) => {
    eb.emit("swipe", e);
  };
  const swipeLeft = (e: HammerInput) => {
    eb.emit("swipeLeft", e);
  };
  const swipeRight = (e: HammerInput) => {
    eb.emit("swipeRight", e);
  };
  const swipeUp = (e: HammerInput) => {
    eb.emit("swipeUp", e);
  };
  const swipeDown = (e: HammerInput) => {
    eb.emit("swipeDown", e);
  };

  const tap = (e: HammerInput) => {
    eb.emit("tap", e);
  };

  const tapOutSide = (e: Event) => {
    if (!e.target || el == e.target || el.contains(e.target as Node)) {
      return;
    }
    eb.emit("tapOutSide", e);
  };

  const transitionstart = (e: TransitionEvent) => {
    eb.emit("transitionstart", e);
  };
  const transitionend = (e: TransitionEvent) => {
    eb.emit("transitionend", e);
  };
  const animationstart = (e: AnimationEvent) => {
    eb.emit("animationstart", e);
  };
  const animationend = (e: AnimationEvent) => {
    eb.emit("animationend", e);
  };

  let _rect = {
    ...el.getBoundingClientRect(),
    width: el.offsetWidth,
    height: el.offsetHeight
  };

  return {
    el,
    get rect() {
      return _rect;
    },
    mousePoint,
    eb,
    start: async function () {
      removes.push(useEventListener(el, "mousedown", mouseDown));
      removes.push(useEventListener(el, "mouseup", mouseUp));
      removes.push(useEventListener(el, "mousemove", mouseMove));
      removes.push(useEventListener(el, "mouseover", mouseOver));
      removes.push(useEventListener(el, "mouseout", mouseOut));
      removes.push(useEventListener(el, "mouseenter", mouseEnter));
      removes.push(useEventListener(el, "mouseleave", mouseLeave));
      removes.push(useEventListener(el, "click", mouseClick));
      removes.push(useEventListener(el, "dblclick", mouseDoubleClick));
      removes.push(
        useEventListener(
          el,
          "onwheel" in document.createElement("div")
            ? "wheel" // modern browsers
            : document.onmousewheel !== undefined
            ? "mousewheel" // Webkit and IE
            : "DOMMouseScroll", // older Firefox
          mouseWheel
        )
      );
      removes.push(useEventListener(el, "contextmenu", mouseContextMenu));

      removes.push(useEventListener(el, "touchstart", touchStart));
      removes.push(useEventListener(el, "touchend", touchEnd));
      removes.push(useEventListener(el, "touchcancel", touchCancel));
      removes.push(useEventListener(el, "touchmove", touchMove));

      removes.push(useEventListener(el, "pointerdown", pointerDown));
      removes.push(useEventListener(el, "pointerup", pointerUp));
      removes.push(useEventListener(el, "pointercancel", pointerCancel));
      removes.push(useEventListener(el, "pointermove", pointerMove));

      hammer.on("pan", pan);
      hammer.on("panstart", panStart);
      hammer.on("panmove", panMove);
      hammer.on("panend", panEnd);
      hammer.on("pancancel", panCancel);
      hammer.on("panleft", panLeft);
      hammer.on("panright", panRight);
      hammer.on("panup", panUp);
      hammer.on("pandown", panDown);

      hammer.on("pinch", pinch);
      hammer.on("pinchin", pinchin);
      hammer.on("pinchout", pinchout);

      hammer.on("press", press);

      hammer.on("rotate", rotate);

      hammer.on("swipe", swipe);
      hammer.on("swipeleft", swipeLeft);
      hammer.on("swiperight", swipeRight);
      hammer.on("swipeup", swipeUp);
      hammer.on("swipedown", swipeDown);

      hammer.on("tap", tap);

      // tapOutSide
      removes.push(useEventListener(document, "pointerdown", tapOutSide));
      // if (isSupportTouch()) {
      //   removes.push(useEventListener(document, "touchstart", tapOutSide));
      // } else {
      //   removes.push(useEventListener(document, "mousedown", tapOutSide));
      // }

      // transition + animation
      removes.push(useEventListener(el, "transitionstart", transitionstart));
      removes.push(useEventListener(el, "transitionend", transitionend));
      removes.push(useEventListener(el, "animationstart", animationstart));
      removes.push(useEventListener(el, "animationend", animationend));

      // resize
      const end = await useResize(el, (rect) => {
        _rect = rect;
        eb.emit("resize", rect);
      });
      removes.push(end);

      // mousePoint 鼠标相对el的位置
      removes.push(
        usePressDownMove(this, {
          start: (e) => {
            ump.setStartMousePoint(getMousePositionInElement(e, el), true);
          },
          move: (e) => {
            ump.setCurrentMousePoint(getMousePositionInElement(e, el));
          },
          end: (e) => {
            ump.setCurrentMousePoint(getMousePositionInElement(e, el), false);
          }
        })
      );
    },
    end: () => {
      eb.removeAllListeners();

      removes.forEach((r) => r());
      removes = [];

      hammer.off("pan", pan);
      hammer.off("panstart", panStart);
      hammer.off("panmove", panMove);
      hammer.off("panend", panEnd);
      hammer.off("pancancel", panCancel);
      hammer.off("panleft", panLeft);
      hammer.off("panright", panRight);
      hammer.off("panup", panUp);
      hammer.off("pandown", panDown);

      hammer.off("pinch", pinch);
      hammer.off("pinchin", pinchin);
      hammer.off("pinchout", pinchout);

      hammer.off("press", press);

      hammer.off("rotate", rotate);

      hammer.off("swipe", swipe);
      hammer.off("swipeleft", swipeLeft);
      hammer.off("swiperight", swipeRight);
      hammer.off("swipeup", swipeUp);
      hammer.off("swipedown", swipeDown);

      hammer.off("tap", tap);
    }
  };
}

type Options = {
  start?: (e: MousePointEvent) => void;
  move?: (e: MousePointEvent) => void;
  end?: (e: MousePointEvent) => void;
};

export function usePressDownMove(
  controller: ElementEventBusController,
  options?: Options
): () => void {
  return usePressDownMoveWithPointer(controller, options);
  // if (isSupportTouch()) {
  //   return usePressDownMoveWithTouch(controller, options);
  // } else {
  //   return usePressDownMoveWithMouse(controller, options);
  // }
}

export function usePressDownMoveWithPointer(
  controller: ElementEventBusController,
  options?: Options
): () => void {
  const eb = controller.eb;

  const start = (e: PointerEvent) => {
    // e.preventDefault();
    // e.stopPropagation();
    eb.on("pointerMove", move);
    eb.on("pointerUp", end);
    eb.on("pointerCancel", end);
    options?.start?.(e);
  };

  const end = (e: PointerEvent) => {
    // e.preventDefault();
    // e.stopPropagation();
    eb.off("pointerMove", move);
    eb.off("pointerUp", end);
    eb.off("pointerCancel", end);
    options?.end?.(e);
  };

  const move = (e: PointerEvent) => {
    // e.preventDefault();
    // e.stopPropagation();
    options?.move?.(e);
  };

  eb.on("pointerDown", start);

  return () => eb.off("pointerDown", start);
}

export function usePressDownMoveWithMouse(
  controller: ElementEventBusController,
  options?: Options
): () => void {
  const eb = controller.eb;

  const start = (e: MouseEvent) => {
    // e.preventDefault();
    // e.stopPropagation();
    document.addEventListener("mousemove", move, false);
    document.addEventListener("mouseup", end, false);
    options?.start?.(e);
  };

  const end = (e: MouseEvent) => {
    // e.preventDefault();
    // e.stopPropagation();
    document.removeEventListener("mousemove", move, false);
    document.removeEventListener("mouseup", end, false);
    options?.end?.(e);
  };

  const move = (e: MouseEvent) => {
    // e.preventDefault();
    // e.stopPropagation();
    options?.move?.(e);
  };

  eb.on("mouseDown", start);

  return () => eb.off("mouseDown", start);
}

export function usePressDownMoveWithTouch(
  controller: ElementEventBusController,
  options?: Options
): () => void {
  const eb = controller.eb;

  const start = (e: TouchEvent) => {
    // e.preventDefault();
    // e.stopPropagation();
    eb.on("touchMove", move);
    eb.on("touchEnd", end);
    eb.on("touchCancel", end);
    const touch = e.changedTouches[0]; // first touch
    options?.start?.(touch);
  };

  const end = (e: TouchEvent) => {
    // e.preventDefault();
    // e.stopPropagation();
    eb.off("touchMove", move);
    eb.off("touchEnd", end);
    eb.off("touchCancel", end);
    const touch = e.changedTouches[0]; // first touch
    options?.end?.(touch);
  };

  const move = (e: TouchEvent) => {
    // e.preventDefault();
    // e.stopPropagation();
    const touch = e.changedTouches[0]; // first touch
    options?.move?.(touch);
  };

  eb.on("touchStart", start);

  return () => eb.off("touchStart", start);
}
